/**
 * Renders an iframe and syncs URLs between browser and iframe.
 *
 * Browser → Iframe: Extracts relative path from browser URL, appends to iframe base URL
 * Iframe → Browser: Listens for URL_SYNC messages and updates browser URL via history.replaceState
 *
 * basePath is used for URL extraction/sync. See RouteConfig in url.ts for details.
 */

import { useLocation, useSearchParams } from "react-router-dom";
import { StyledIframe } from "../styles";
import { addQueryParams, addRelativePath, getRelativePath } from "../utils/url";
import { useUrlSyncListener } from "../hooks/useUrlSync";
import useSendLastViewedDashboard from "../hooks/useSendLastViewedDashboard";

interface LocationState {
  iFrameUrl: string;
  id: string;
  path: string;
}

interface IFramePageProps {
  /** Base path for URL extraction/sync. See RouteConfig in url.ts. */
  basePath?: string;
  iframe: {
    id: string;
    url: string;
    key: string;
  };
}

const IFramePage = ({ basePath, iframe }: IFramePageProps) => {
  const iframeRef = useSendLastViewedDashboard(iframe.id);
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  useUrlSyncListener(basePath);

  let fullUrl = state?.iFrameUrl ? state.iFrameUrl : iframe.url;
  // Handles special routing case for edit profile
  if (iframe.id === "Admin") {
    fullUrl = `${iframe.url}#${state?.path}${state?.id}`;
  }

  const relativePath = basePath
    ? getRelativePath(location.pathname, basePath)
    : undefined;
  const pathedUrl = addRelativePath(fullUrl, relativePath);

  const [searchParams] = useSearchParams();
  const srcUrl = addQueryParams(pathedUrl, searchParams);

  return (
    <StyledIframe
      ref={iframeRef}
      key={iframe.key}
      title={iframe.id}
      id={iframe.id}
      src={srcUrl}
      allow="clipboard-write; notifications"
    />
  );
};

export default IFramePage;
